import {
  FetchApiError,
  fetchApiDeleteV2,
  fetchApiGetV2,
  fetchApiPostV2,
  fetchApiPutV2,
} from "../../../../api/api";
import { AllComponents } from "../../../../components/_default/interfaces/base";
import { Set } from "immutable";
import store from "../../../store";
import {
  addNewTemplate,
  removeTemplateByRootUuid,
  updateTemplates,
} from "./templateActions";
import {
  getTemplateMutationKey,
  mergeHeaderFooterTemplatesFromStructure,
} from "./templateHelpers";
import {
  createUuidV7,
  isBuilderEditorSentinelComponent,
  mapStructureMapToApiPutPayload,
  mapWebpageComponentsFromApi,
} from "../structure/structureIdentity";
import type { StructureMap } from "../structure/structureReducer";
/** Stored API value for generic templates (replaces empty `use` and legacy `"Default"`). */
export const TEMPLATE_USE_GENERAL = "General";

/** Default API `languageCode` for sidebar-created header/footer templates when locale does not match an active language row. */
export const DEFAULT_NEW_HEADER_FOOTER_TEMPLATE_LANGUAGE_CODE = "et";

export function normalizeTemplateUseValue(
  use: string | null | undefined
): string {
  const u = typeof use === "string" ? use.trim() : "";
  if (!u) return TEMPLATE_USE_GENERAL;
  if (u.toLowerCase() === "default") return TEMPLATE_USE_GENERAL;
  return u;
}

export interface Template {
  name: string;
  use: string;
  languageCode?: string | null;
  visible: boolean;
  components?: Record<string, any>;
  rootComponentUuid?: string;
  webpages: Array<number | string>;
  component: AllComponents | null;
}

/** POST /templates body: UUID-only tree; not assignable to strict AllComponents. */
type TemplateCreatePayloadTree = Record<string, unknown>;

type CreateTemplateInput = Partial<Template> & {
  name: string;
  component?: AllComponents | null;
  webpage_component?: AllComponents | null;
  /** @deprecated Prefer `use`; still accepted for older callers */
  type?: string;
};

function cloneTemplateSubtreeWithFreshUuids(
  source: AllComponents | null | undefined,
  structureComponents: StructureMap["components"],
  options: { preserveSourceUuids?: boolean } = {}
): { root: TemplateCreatePayloadTree; components: Record<string, TemplateCreatePayloadTree> } | null {
  if (!source) return null;
  if (isBuilderEditorSentinelComponent(source)) return null;
  const preserveSourceUuids = !!options.preserveSourceUuids;
  const components: Record<string, TemplateCreatePayloadTree> = {};
  const sourceByUuid = new Map<string, AllComponents>();
  const sourceById = new Map<string, AllComponents>();

  for (const component of Object.values(structureComponents ?? {})) {
    if (!component) continue;
    if (isBuilderEditorSentinelComponent(component)) continue;
    if (component.uuid?.trim()) {
      sourceByUuid.set(component.uuid.trim(), component);
    }
    if (component.id != null) {
      sourceById.set(String(component.id), component);
    }
  }

  const cloneNode = (
    node: AllComponents,
    parentUuid: string | null
  ): TemplateCreatePayloadTree => {
    const directChildren = (
      Array.isArray(node.childElements) ? node.childElements : []
    ).filter((c) => !isBuilderEditorSentinelComponent(c));
    const relationChildren =
      directChildren.length > 0
        ? []
        : (node.childWebpageComponentRelations ?? [])
            .filter(
              (relation) =>
                !!relation &&
                typeof relation === "object" &&
                relation.child_id !== -11
            )
            .map((relation) => {
              const relationUuid =
                typeof relation?.child_uuid === "string"
                  ? relation.child_uuid.trim()
                  : "";
              if (relationUuid && sourceByUuid.has(relationUuid)) {
                return sourceByUuid.get(relationUuid) ?? null;
              }
              if (relation?.child_id != null) {
                return sourceById.get(String(relation.child_id)) ?? null;
              }
              return null;
            })
            .filter(
              (child): child is AllComponents =>
                !!child && !isBuilderEditorSentinelComponent(child)
            );
    const children = directChildren.length > 0 ? directChildren : relationChildren;
    const sourceUuid = node.uuid?.trim() ?? "";
    const preferredUuid = preserveSourceUuids && sourceUuid ? sourceUuid : "";
    const uuid =
      preferredUuid && !components[preferredUuid]
        ? preferredUuid
        : createUuidV7();
    const clonedChildren = children.map((child) => cloneNode(child, uuid));

    const clone = {
      ...(node as unknown as Record<string, unknown>),
      uuid,
      isShared: false,
      parent_uuid: parentUuid,
      childElements: clonedChildren,
      childWebpageComponentRelations: clonedChildren.map((child, index) => ({
        id: 0,
        parent_uuid: uuid,
        child_uuid: child.uuid,
        sort: index + 1,
        created_at: null,
        updated_at: null,
      })),
    } as TemplateCreatePayloadTree;

    delete clone.id;
    delete clone.parent_id;
    const relations = clone.childWebpageComponentRelations as unknown[];
    if (Array.isArray(relations)) {
      for (const relation of relations) {
        if (relation && typeof relation === "object") {
          delete (relation as Record<string, unknown>).parent_id;
          delete (relation as Record<string, unknown>).child_id;
        }
      }
    }

    components[uuid] = clone;
    return clone;
  };

  const root = cloneNode(source, null);
  return { root, components };
}

/** Resolve builder `activeLanguages` row id from API `languageCode` (e.g. `et`, `et_EE`). */
export function resolveTemplateLanguageIdFromCode(
  languageCode: unknown
): number | undefined {
  if (typeof languageCode !== "string" || !languageCode.trim()) {
    return undefined;
  }
  const code = languageCode.trim().toLowerCase();
  const langs =
    ((store.getState() as any)?.builder?.languageSettings?.activeLanguages ??
      []) as Array<{
      id: number;
      locale: string;
      language: { shortname: string };
    }>;
  for (const lang of langs) {
    if (lang.language?.shortname?.toLowerCase() === code) {
      return lang.id;
    }
    const loc = (lang.locale ?? "").toLowerCase();
    if (loc === code) {
      return lang.id;
    }
    const primary = loc.split("_")[0];
    if (primary === code) {
      return lang.id;
    }
  }
  return undefined;
}

function parseTemplatesApiResponse(response: unknown): {
  templates: Template[];
} {
  const normalizeTemplate = (value: unknown): Template | null => {
    if (!value || typeof value !== "object") {
      return null;
    }
    const template = value as Record<string, unknown>;
    const rootComponentUuidRaw =
      typeof template.rootComponentUuid === "string"
        ? template.rootComponentUuid.trim()
        : "";
    const legacyRootUuidRaw =
      typeof template.rootComponentUuid === "string"
        ? template.rootComponentUuid.trim()
        : "";
    const rootComponentUuid = rootComponentUuidRaw || legacyRootUuidRaw || undefined;
    const sourceComponents =
      template.components && typeof template.components === "object"
        ? (template.components as StructureMap["components"])
        : {};
    const normalizedStructure = mapWebpageComponentsFromApi({
      components: sourceComponents,
      webpageComponents: rootComponentUuid ? [rootComponentUuid] : [],
    });

    const apiLanguageCode =
      typeof template.languageCode === "string" ? template.languageCode : undefined;

    const useRaw =
      typeof template.use === "string" ? template.use.trim() : "";
    const legacyTypeAsUse =
      typeof template.type === "string" ? template.type.trim() : "";
    const resolvedUse = normalizeTemplateUseValue(
      useRaw || legacyTypeAsUse
    );

    const { type: _legacyApiTemplateType, ...templateRest } = template;

    return {
      ...templateRest,
      name: typeof template.name === "string" ? template.name : "",
      use: resolvedUse,
      visible: typeof template.visible === "boolean" ? template.visible : true,
      webpages: Array.isArray(template.webpages)
        ? (template.webpages as Array<number | string>)
        : [],
      components: normalizedStructure.components as Record<string, unknown>,
      rootComponentUuid: rootComponentUuid,
      languageCode: apiLanguageCode ?? DEFAULT_NEW_HEADER_FOOTER_TEMPLATE_LANGUAGE_CODE,
    } as Template;
  };

  if (Array.isArray(response)) {
    return {
      templates: response
        .map((entry) => normalizeTemplate(entry))
        .filter((entry): entry is Template => !!entry),
    };
  }

  if (!response || typeof response !== "object") {
    return {
      templates: [],
    };
  }
  const value = response as { templates?: unknown };
  return {
    templates: Array.isArray(value.templates)
      ? value.templates
          .map((entry) => normalizeTemplate(entry))
          .filter((entry): entry is Template => !!entry)
      : [],
  };
}

function templatesArrayFromStore(): Template[] {
  return (store.getState().builder.templates as Set<Template>).toArray();
}

/** Merge header/footer template trees from a saved page structure into Redux (no GET). */
export function syncHeaderFooterTemplatesFromStructure(
  structure: StructureMap
): void {
  const list = templatesArrayFromStore();
  updateTemplates(mergeHeaderFooterTemplatesFromStructure(list, structure));
}

function pickTemplateFromStore(
  hint: Pick<Template, "name"> &
    Partial<Pick<Template, "rootComponentUuid">>
): Template | undefined {
  const templates = store.getState().builder.templates as Set<Template>;
  const u = hint.rootComponentUuid?.trim();
  if (u) {
    const byUuid = templates.find((t) => t.rootComponentUuid?.trim() === u);
    if (byUuid) return byUuid;
  }
  return templates.find((t) => t.name === hint.name);
}

const TemplateAPI = {
  loadTemplates,
  syncHeaderFooterTemplatesFromStructure,
  addTemplate,
  createTemplate,
  saveTemplateById,
  deleteTemplate,
};
export default TemplateAPI;

export function loadTemplates() {
  return fetchApiGetV2<unknown>(`/templates`, {}).then((res) => {
    const parsed = parseTemplatesApiResponse(res);
    return updateTemplates(parsed.templates);
  });
}

export function addTemplate(data: { template: string; name: string }) {
  return fetchApiPostV2<Array<Template>[0]>(`/templates`, data).then((d) =>
    addNewTemplate(d)
  );
}

export function createTemplate(data: CreateTemplateInput) {
  const fromUseOrType =
    typeof data.use === "string" && data.use.trim()
      ? data.use.trim()
      : typeof data.type === "string" && data.type.trim()
        ? data.type.trim()
        : "";
  const normalizedUse = normalizeTemplateUseValue(fromUseOrType);
  const templateKind = normalizedUse.toLowerCase();
  const preserveSourceUuids =
    templateKind === "header" || templateKind === "footer";

  const structureComponents =
    store.getState().builder.structure?.components ?? {};
  const duplicatedTree = cloneTemplateSubtreeWithFreshUuids(
    (data.webpage_component as AllComponents | null | undefined) ??
      (data.component as AllComponents | null | undefined) ??
      null,
    structureComponents,
    { preserveSourceUuids }
  );
  const rootUuidRaw = duplicatedTree?.root.uuid;
  const templateComponentUuid =
    typeof rootUuidRaw === "string" && rootUuidRaw.trim() !== ""
      ? rootUuidRaw
      : createUuidV7();
  if (
    duplicatedTree &&
    (templateKind === "header" || templateKind === "footer")
  ) {
    (duplicatedTree.root as TemplateCreatePayloadTree & { isShared?: boolean })
      .isShared = true;
  }
  const apiTemplateComponents = duplicatedTree
    ? mapStructureMapToApiPutPayload({
        components: duplicatedTree.components as unknown as StructureMap["components"],
        webpageComponents: [templateComponentUuid],
      })
    : null;

  const payload: Record<string, unknown> = {
    name: data.name,
    use: normalizedUse,
    languageCode:
      typeof data.languageCode === "string" ? data.languageCode : null,
    visible: data.visible ?? true,
      ...(apiTemplateComponents
      ? {
          components: Object.values(apiTemplateComponents.components),
        }
      : {}),
  };

  return fetchApiPostV2<unknown>(`/templates`, payload).then(() =>
    loadTemplates().then(() =>
      pickTemplateFromStore({
        name: data.name,
        rootComponentUuid: templateComponentUuid,
      })
    )
  );
}

function resolveTemplateRootComponentUuid(data: Template): string {
  const fromFields =
    (typeof data.rootComponentUuid === "string" && data.rootComponentUuid.trim()) ||
    (data.component as AllComponents | null | undefined)?.uuid?.trim() ||
    "";
  if (fromFields) {
    return fromFields;
  }
  const comps = data.components;
  if (!comps || typeof comps !== "object") {
    return "";
  }
  for (const c of Object.values(comps)) {
    if (!c || typeof c !== "object") continue;
    const pu = (c as { parent_uuid?: string | null }).parent_uuid;
    if (pu != null && String(pu).trim() !== "") continue;
    const u = (c as AllComponents).uuid?.trim();
    if (u) return u;
  }
  return "";
}

/**
 * Template PUT must send the same component map shape as webpage PUT
 * ({@link mapStructureMapToApiPutPayload}), not the in-memory client tree
 * (`childWebpageComponentRelations`, `parent_uuid`, etc.).
 */
function applyApiComponentShapeToTemplatePayload(
  payload: Record<string, unknown>,
  data: Template
): void {
  const raw = data.components;
  if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0) {
    return;
  }
  const rootUuid = resolveTemplateRootComponentUuid(data);
  const apiStructure = mapStructureMapToApiPutPayload({
    components: raw as StructureMap["components"],
    webpageComponents: rootUuid ? [rootUuid] : [],
  });
  payload.components = apiStructure.components;
  const resolvedRoot =
    (apiStructure.webpageComponents[0] as string | undefined)?.trim() ||
    rootUuid;
  if (resolvedRoot) {
    payload.rootComponentUuid = resolvedRoot;
  }
  delete payload.component;
  delete payload.webpage_component;
}

function deleteTemplate(
  templateOrKey:
    | Template
    | Pick<Template, "rootComponentUuid">
    | string
    | number
) {
  const key =
    typeof templateOrKey === "string" || typeof templateOrKey === "number"
      ? String(templateOrKey)
      : getTemplateMutationKey(templateOrKey);
  return fetchApiDeleteV2<unknown>(
    `/templates/${encodeURIComponent(key)}`,
    {}
  ).then(() => {
    removeTemplateByRootUuid(key);
    return templatesArrayFromStore();
  });
}

function saveTemplateById(
  first: number | Template,
  second?: Template
): Promise<Template | undefined> {
  const data = second ?? (first as Template);
  const key = getTemplateMutationKey(data);
  const payload: Record<string, unknown> = { ...data };
  if (data.rootComponentUuid) {
    payload.rootComponentUuid = data.rootComponentUuid;
  }
  delete payload.type;
  if (typeof payload.use === "string") {
    payload.use = normalizeTemplateUseValue(payload.use);
  }
  applyApiComponentShapeToTemplatePayload(payload, data);
  return fetchApiPutV2<unknown>(
    `/templates/${encodeURIComponent(key)}`,
    payload
  ).then(() =>
    loadTemplates().then(() => pickTemplateFromStore(data))
  );
}

export function isTemplateDeleteConflictError(
  error: unknown
): error is FetchApiError {
  return error instanceof FetchApiError && error.status === 409;
}

export function parseTemplateDeleteConflictMessage(error: FetchApiError): {
  message: string;
  details: string[];
} {
  const body = error.body as
    | { message?: string; errors?: { template?: string[] } }
    | undefined;
  const message =
    typeof body?.message === "string" && body.message.trim() !== ""
      ? body.message
      : error.message;
  const raw = body?.errors?.template;
  const details = Array.isArray(raw)
    ? raw.filter((s): s is string => typeof s === "string")
    : [];
  return { message, details };
}
